export type CSRFObject = { name: string; value: string };

/**
 * Stores CSRF tokens obtained from API responses.
 *
 * Supports two token sources:
 * - `saved`: CSRF object from response body meta (name + value)
 * - `xsrfToken`: plaintext CSRF token from the X-CSRF-TOKEN response header,
 *   sent back as X-CSRF-TOKEN on requests when the XSRF-TOKEN cookie is not
 *   readable (e.g. cross-domain deployments where JS cannot access the cookie)
 *
 * The backend exposes the header when CSRF_TOKEN_IN_HEADER=true is set.
 */
export class CsrfStorage {
  private static _saved: CSRFObject = {
    name: '',
    value: '',
  };

  // plaintext CSRF token obtained from the X-CSRF-TOKEN response header
  private static _xsrfToken: string = '';

  // overridden by AppState to sync CSRF data into the query cache
  public static onChange(_v: CSRFObject) {}

  public static set saved(value: CSRFObject) {
    this._saved = value;
    this.onChange(value);
  }

  public static get saved() {
    return this._saved;
  }

  public static set xsrfToken(value: string) {
    this._xsrfToken = value;
  }

  public static get xsrfToken() {
    return this._xsrfToken;
  }

  /**
   * Resets all stored tokens. Called during logout to prevent stale tokens
   * from being sent after session invalidation.
   */
  public static clear() {
    this._saved = { name: '', value: '' };
    this._xsrfToken = '';
  }

  /**
   * Returns the CSRF header name and value to attach to outgoing requests.
   *
   * When a plaintext token was obtained from the X-CSRF-TOKEN response header
   * (cross-domain), returns { name: 'X-CSRF-TOKEN', value }. Otherwise
   * returns null so the caller can fall back to the cookie-based X-XSRF-TOKEN.
   *
   * The stored token persists until explicitly rotated by a new response that
   * includes the X-CSRF-TOKEN header, or cleared on logout via `clear()`.
   * Responses that omit the header (e.g. most GET responses) do NOT clear the
   * token — the last known value remains valid until the backend rotates it.
   */
  public static getCsrfHeader(): { name: string; value: string } | null {
    if (this._xsrfToken) {
      return { name: 'X-CSRF-TOKEN', value: this._xsrfToken };
    }
    return null;
  }

  public isReady() {
    return !!CsrfStorage.saved.value;
  }
}
